import { doc, getDoc, getDocs, limit, orderBy, query } from "firebase/firestore";
import { businessCollection } from "./firestorePaths";
import { db } from "@lib/firebase";
import { normalizeOrders, normalizeOrder } from "@/views/admin/orders/utils/normalizeOrdersData";
import { fetchBusinessSettings } from "./businessApi";

// businesses/{id}/orders. The stored shape is the POS order model; the views
// expect the richer shape normalizeOrdersData.js produces, so each document is
// mapped into that raw shape first and then run through the existing
// normalizer - the view layer and its validation stay untouched.

const MAX_ORDERS = 500;

const str = (v) => (typeof v === "string" ? v.trim() : "");
const num = (v, fallback = 0) =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;

const fullName = (c = {}) =>
  [str(c.firstName), str(c.lastName)].filter(Boolean).join(" ") || str(c.email);

const iso = (ms) => (ms ? new Date(ms).toISOString() : "");

/** Latest entry in the states timeline, which drives fulfillment status. */
function latestState(states) {
  if (!Array.isArray(states) || !states.length) return null;
  return states.reduce((a, b) => (num(a.date) > num(b.date) ? a : b));
}

/** "State_Pending" -> "pending" */
const stateKey = (s) => str(s).replace(/^State_/, "").toLowerCase();

/**
 * order.status holds a *state id*, not a label - either a built-in like
 * "State_Confirmed" or a business-defined uuid. Both resolve against
 * settings.states, where each entry is {id, title}. Falls back to the raw
 * value so an unrecognised id still renders as something.
 */
function resolveStatus(status, states = []) {
  const raw = str(status);
  if (!raw) return "";
  const match = states.find((s) => str(s.id) === raw);
  if (match) return str(match.title).toLowerCase();
  return stateKey(raw);
}

function paymentStatus(payments, total) {
  const paid = payments.reduce((acc, p) => acc + num(p.amount), 0);
  if (!payments.length || paid <= 0) return "unpaid";
  if (paid >= total) return "paid";
  return "pending";
}

/** Firestore document -> the raw shape normalizeOrder() consumes. */
function toRawOrder(id, data = {}, staff = {}, states = []) {
  const totals = data.totals || {};
  const items = Array.isArray(data.items) ? data.items : [];
  const payments = Array.isArray(data.payments) ? data.payments : [];
  const createdAt = num(data.createdAt) || num(data.metadata?.createdAt);
  const total = num(totals.total);
  const amountPaid = payments.reduce((acc, p) => acc + num(p.amount), 0);
  const state = latestState(data.states);
  const fulfillment = data.fulfillment || {};
  const isPickup = str(fulfillment.method).toLowerCase() === "pickup";

  return {
    id,
    orderNumber: str(data.orderId) || id,
    createdAt: iso(createdAt),
    orderDate: iso(createdAt),
    orderStatus: resolveStatus(data.status, states),
    fulfillmentStatus: resolveStatus(state?.state, states),
    paymentStatus: paymentStatus(payments, total),
    salesChannel: str(fulfillment.method),
    items: items.reduce((acc, i) => acc + num(i.quantity, 1), 0),
    customer: {
      id: str(data.customer?.email),
      name: fullName(data.customer),
      email: str(data.customer?.email),
      phone: data.customer?.phone ? String(data.customer.phone) : "",
    },
    // The POS records a pickup location or a delivery line, not a structured
    // address, so only the fields that actually exist are filled.
    shippingAddress: {
      name: fullName(data.customer),
      addressLine1: isPickup
        ? str(fulfillment.pickup?.locationTitle)
        : str(fulfillment.delivery),
      city: str(fulfillment.pickup?.address),
    },
    shippingMethod: {
      type: str(fulfillment.method),
      name: isPickup ? "Pickup" : "Delivery",
      fee: num(totals.shipping),
    },
    paymentDetails: {
      method: str(payments[0]?.methodLabel) || str(payments[0]?.method),
      status: paymentStatus(payments, total),
      transactionId: str(payments[0]?.gatewayReference),
      paymentDate: iso(num(payments[0]?.paidAt) || num(payments[0]?.date)),
      amountPaid,
    },
    pricing: {
      subTotal: num(totals.subtotal),
      discounts: num(totals.discount),
      taxes: num(totals.tax),
      shipping: num(totals.shipping),
      total,
      amountPaid,
    },
    products: items.map((i) => ({
      variantComboId: str(i.variantId),
      productName: str(i.name),
      sku: str(i.id),
      quantity: num(i.quantity),
      unitPrice: num(i.unitPrice),
      total: num(i.lineTotal),
      image: str(i.image),
    })),
    notes: str(data.notes),
    // Not part of the normalized shape, but useful on detail views.
    staffName: staff[data.transactor]?.name || str(data.transactor),
    currency: str(totals.currency),
  };
}

export async function fetchOrdersList(businessId, staff = {}) {
  if (!businessId) return [];

  // Settings carry the state list that turns status ids into readable labels.
  const [settings, snap] = await Promise.all([
    fetchBusinessSettings(businessId),
    getDocs(
      query(
        businessCollection(businessId, "orders"),
        orderBy("createdAt", "desc"),
        limit(MAX_ORDERS)
      )
    ),
  ]);

  const states = settings?.states || [];
  return normalizeOrders(snap.docs.map((d) => toRawOrder(d.id, d.data(), staff, states)));
}

export async function fetchOrderById(businessId, orderId, staff = {}) {
  if (!businessId || !orderId) return null;
  const [settings, snap] = await Promise.all([
    fetchBusinessSettings(businessId),
    getDoc(doc(db, "businesses", businessId, "orders", orderId)),
  ]);
  return snap.exists()
    ? normalizeOrder(toRawOrder(snap.id, snap.data(), staff, settings?.states || []))
    : null;
}

/** Home dashboard widget - newest few, in the flat shape that table validates. */
export async function fetchRecentOrders(businessId, staff = {}, count = 8) {
  const orders = await fetchOrdersList(businessId, staff);
  return orders.slice(0, count).map((o) => ({
    id: o.id,
    customerName: o.customer.name || "Walk-in",
    status: o.orderStatus || "processing",
    total: o.pricing.total,
    date: o.createdAt,
  }));
}
